import {
  fetchAwaitingUploadDevices,
  fetchTicketCountAwaitingUploadDevices,
  fetchClinicDevices,
  fetchTicketCountClinicDevices,
  fetchMaintenanceDevices,
  fetchRmaDevices,
  actionIconFormatter,
  awaitingUploadDevicesFormatter,
  chargingStateFormatter,
  deviceIdFormatter,
  deviceLastStudyIdFormatter,
  deviceLastUsedFormatter,
  devicesFormatter,
  facilityFormatter,
  filterUnusedValue,
  fwVersionFormatter,
  lastConnectFormatter,
  maintenanceDevicesFormatter,
  modelFormatter,
  networkModeFormatter,
  operationStatusFormatter,
  rmaDevicesFormatter,
  statusFormatter,
} from '@/entities/device';
import { useRmaStore } from '@/entities/rma';
import { DEVICE_TABLE_TYPE, EMITTER_CONSTANTS } from '@/shared/config';
import {
  checkIsChangedSorter,
  convertSortOrder,
  renderSortIcon,
  emitter,
  showMessageError,
  showMessageInfo,
  GETTEXT_ALL_FACILITIES,
  appearDataTable,
  preventAppScroll,
} from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { createElement, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import { facilitySelection } from '@/entities/facility';
import type { DevicesTableProps } from './devicesTable.types';

const VISUALLY_HIDDEN_STYLE = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
} as const;

/**
 * Drives one devices-table tab: loading, paging, sorting, search and multi-selection for RMA tickets.
 * Seeds the device filter from the tab type and search, fetches pages through the matching device
 * query, refetches on activation, facility and search changes, keeps rows live from the last-sync and
 * device-event emitters, and builds the columns and Protect Plus RMA navigation.
 *
 * @param props - Tab type and activity, search and model selections, selected and supported
 *   facilities, the unused filter flag and the drawer and notification callbacks.
 * @returns The table state, maintenance flag, checked ids, row selection, filter ref, column
 *   builder and the drawer, selection, Protect Plus and table-change handlers.
 */
export const useDevicesTable = ({
  isActive,
  type,
  eventsNoti,
  toggleDisplayDrawer,
  searchGoto,
  selectedFacility,
  searchByValue: searchByValueProp,
  searchValue: searchValueProp,
  supportFacilities,
  model: modelProp,
  selectGoto,
  isUnused,
}: Pick<
  DevicesTableProps,
  | 'isActive'
  | 'type'
  | 'eventsNoti'
  | 'toggleDisplayDrawer'
  | 'searchGoto'
  | 'selectedFacility'
  | 'searchByValue'
  | 'searchValue'
  | 'supportFacilities'
  | 'model'
  | 'selectGoto'
  | 'isUnused'
>) => {
  const navigate = useNavigate();

  /**
   * The RMA limit config held in the RMA store, read reactively by selector. Supplies the maximum number of devices that may go on one ticket.
   *
   * @returns The store's rmaLimitConfig value.
   */
  const rmaLimitConfig = useRmaStore((s) => s.rmaLimitConfig);

  const isLostOrInactiveTab = type === DEVICE_TABLE_TYPE.LOST_INACTIVE;
  const isInStudySubtab = type === DEVICE_TABLE_TYPE.IN_STUDY;
  const isAwaitingUploadSubtab = type === DEVICE_TABLE_TYPE.AWAITING_UPLOAD;
  const isNotInStudySubtab = type === DEVICE_TABLE_TYPE.NOT_IN_STUDY;
  const isRmaSubtab = type === DEVICE_TABLE_TYPE.RMA;
  const isMaintenanceSubtab = type === DEVICE_TABLE_TYPE.MAINTENANCE;

  const filterObject = useRef<any>({
    additionalStatus: isRmaSubtab ? 'RMA' : undefined,
    isActive: isLostOrInactiveTab ? false : isNotInStudySubtab || undefined,
    isInStudy: isNotInStudySubtab ? false : isInStudySubtab || undefined,
    deviceId: searchGoto.searchByValue === 'deviceId' ? searchGoto.searchValue : undefined,
    fwVersion: searchGoto.searchByValue === 'fwVersion' ? searchGoto.searchValue : undefined,
    status: undefined,
    sortBy: 'deviceId',
    sortOrder: 'desc',
    deviceIdCursor: undefined,
    model: selectGoto.model === 'All models' ? undefined : selectGoto.model,
    canInitProtectPlus: isMaintenanceSubtab,
  });
  const needFetchMoreDevices = useRef(false);
  const hasChangedSearchValue = useRef(false);
  const searchByValue = useRef(searchByValueProp || 'deviceId');
  const searchValue = useRef(searchValueProp || '');
  const lastQueryFilter = useRef({});

  const [state, setState] = useMergeState<any>({
    skeletonDevices: _.map(_.range(10), (item) => ({
      deviceId: item,
    })),
    devices: [],
    isLoadedData: !(
      (facilitySelection.userSupportFacilities()?.length ?? 0) > 0 ||
      (supportFacilities?.length ?? 0) > 0
    ),
    devicesPage: 1,
    sizePerPage: 10,
    isEndOfData: false,
    isDisabledSearchInput: true,
    isShowFacilityName: selectedFacility.value === GETTEXT_ALL_FACILITIES,
    isDrawerStudyInfo: false,
    isDrawerDeviceStatus: false,
    fetchStudyId: undefined,
    checkedDevices: [],
    firstCheckedDeviceFacility: undefined,
    deviceTicketCount: undefined,
  });

  /**
   * The device ids of the currently checked rows, used as the table's selected row keys. Recomputed only when the checked-devices state changes.
   *
   * @returns Device ids extracted from the checked device records.
   */
  const checkedDeviceIds = useMemo(
    () => _.map(state.checkedDevices, (x) => x.deviceId),
    [state.checkedDevices],
  );

  /**
   * Row-selection configuration for the devices table, enabled only on the maintenance sub-tab and undefined elsewhere. Selection changes store each row's device id and model plus the facility of the first checked device, the select-all control is hidden while all facilities are in scope, and checkboxes are disabled for rows whose facility differs from that first checked facility so a ticket never spans facilities.
   *
   * @returns The table's rowSelection config on the maintenance sub-tab, otherwise undefined.
   */
  const rowSelection = useMemo(() => {
    if (isMaintenanceSubtab) {
      const isAllFacilities = selectedFacility?.value === GETTEXT_ALL_FACILITIES;
      return {
        selectedRowKeys: checkedDeviceIds,
        onChange: (selectedRowKeys: any, selectedRows: any) => {
          /**
           * The selection reduced to the fields a Protect+/RMA request needs. Each selected row contributes its device id and its device model.
           *
           * @returns Device id and model pairs for the selected rows.
           */
          const checkedDevices = _.map(selectedRows, (x) => ({
            deviceId: x.deviceId,
            model: x.deviceModel,
          }));
          const objectState: any = { checkedDevices };
          const currentFacility = state.firstCheckedDeviceFacility?.id;
          if (checkedDevices.length === 1 && selectedRows?.[0]?.facility?.id !== currentFacility) {
            objectState.firstCheckedDeviceFacility = selectedRows[0].facility;
          } else if (checkedDevices.length === 0 && currentFacility) {
            objectState.firstCheckedDeviceFacility = undefined;
          } else {
            objectState.firstCheckedDeviceFacility = selectedRows?.[0].facility;
          }
          setState(objectState);
        },
        columnWidth: '48px',
        hideSelectAll: isAllFacilities,
        columnTitle: isAllFacilities
          ? () => createElement('span', { style: VISUALLY_HIDDEN_STYLE }, t`Select device`)
          : undefined,
        getCheckboxProps: (record: any) => {
          const isDisabled =
            state.firstCheckedDeviceFacility &&
            record?.facility?.id !== state.firstCheckedDeviceFacility?.id;
          return {
            disabled: Boolean(isDisabled),
            'aria-label': t`Select device ${record?.deviceId}`,
          };
        },
      };
    }
    return undefined;
  }, [type, checkedDeviceIds, state.firstCheckedDeviceFacility]);

  /**
   * Mirrors the active device filter back into the shared search and select state so the search bar survives navigation. Assigns the filter's model, defaulting to all models, onto the select target and sets searchValue with its matching searchByValue from deviceId or fwVersion, clearing searchValue when neither is set.
   *
   * @returns Nothing.
   */
  const updateSearchRedux = () => {
    _.assign(selectGoto, { model: filterObject.current.model || 'All models' });
    if (filterObject.current.deviceId) {
      _.assign(searchGoto, {
        searchValue: filterObject.current.deviceId,
        searchByValue: 'deviceId',
      });
    } else if (filterObject.current.fwVersion) {
      _.assign(searchGoto, {
        searchValue: filterObject.current.fwVersion,
        searchByValue: 'fwVersion',
      });
    } else {
      _.assign(searchGoto, { searchValue: undefined });
    }
  };

  /**
   * Applies a pushed last-sync update to the currently loaded devices page. Clones the rows, finds the one whose deviceId matches the message and, when found, writes the new lastSync and commits the clone to state; devices outside the loaded page are ignored.
   *
   * @param msg - The pushed message carrying a deviceId and its new lastSync data.
   * @returns Nothing.
   */
  const handleUpdateLastSync = (msg: any) => {
    const newDevices = _.cloneDeep(state.devices);
    /**
     * The cloned device row whose id matches the incoming last-sync message. Undefined when the pushed device is not part of the currently loaded page.
     *
     * @returns The matching device record, or undefined.
     */
    const ele = _.find(newDevices, (x) => x.deviceId === msg.deviceId);
    if (ele) {
      ele.lastSync = msg.lastSync;
      setState({ devices: newDevices });
    }
  };

  /**
   * Applies a pushed device-status update to the currently loaded devices page. Clones the rows, finds the one whose deviceId matches the message and, when found, writes the new status and commits the clone to state; devices outside the loaded page are ignored.
   *
   * @param msg - The pushed message carrying a deviceId and its new status.
   * @returns Nothing.
   */
  const handleUpdateDeviceStatus = (msg: any) => {
    const newDevices = _.cloneDeep(state.devices);
    /**
     * The cloned device row whose id matches the incoming status message. Undefined when the pushed device is not part of the currently loaded page.
     *
     * @returns The matching device record, or undefined.
     */
    const ele = _.find(newDevices, (x) => x.deviceId === msg.deviceId);
    if (ele) {
      ele.status = msg.status;
      setState({ devices: newDevices });
    }
  };

  /**
   * Narrows the devices-table filter down to the fields the awaiting-upload query accepts. Picks sortOrder, deviceId, model, sortBy and deviceIdCursor, maps the first entry of facilityIds to a single facilityId when present, and forces includeRma and isEndOfDuration to true.
   *
   * @param filter - The current devices-table filter object.
   * @returns The filter reduced to the awaiting-upload query fields.
   */
  const formatFilterAwaitingUploadDevices = (filter: any) => {
    const awaitingUploadDevicesFilter = _.pick(filter, [
      'sortOrder',
      'deviceId',
      'model',
      'sortBy',
      'deviceIdCursor',
    ]);

    if (!_.isEmpty(filter?.facilityIds)) {
      _.assign(awaitingUploadDevicesFilter, {
        facilityId: filter.facilityIds?.[0],
      });
    }

    _.assign(awaitingUploadDevicesFilter, {
      includeRma: true,
      isEndOfDuration: true,
    });

    return awaitingUploadDevicesFilter;
  };

  /**
   * Loads the RMA ticket count matching the devices table's current filter. On the awaiting-upload subtab it first narrows the filter to the awaiting-upload fields and queries the awaiting-upload count, otherwise it queries the clinic-devices count; a failure raises an error toast and yields undefined.
   *
   * @param isAwaitingUploadSubtab - Whether the awaiting-upload subtab is active.
   * @param queryFilter - The device filter the count should match.
   * @returns A promise for the ticket count, or undefined when the query fails.
   */
  const fetchTicketCount = async (isAwaitingUploadSubtab: any, queryFilter: any) => {
    try {
      const filter = isAwaitingUploadSubtab
        ? formatFilterAwaitingUploadDevices(queryFilter)
        : queryFilter;

      const ticketCount = isAwaitingUploadSubtab
        ? await fetchTicketCountAwaitingUploadDevices(filter)
        : await fetchTicketCountClinicDevices(filter);
      return ticketCount;
    } catch (error: any) {
      showMessageError({ content: error.message || 'Failed to fetch ticket count' });
      return undefined;
    }
  };

  /**
   * Loads a page of devices for the active devices tab and merges the result into state. Scopes the filter to the selected facility, picks the awaiting-upload, RMA, maintenance or clinic query with its matching formatter, discards responses whose filter is stale, then either appends a cursor page through appearDataTable while tracking end-of-data or replaces the rows and refreshes the ticket count, toasting on error.
   *
   * @param updatedFilterObject - Filter changes merged over the current filter ref.
   * @param isFetchMoreByClickingNext - Whether this fetch continues pagination instead of restarting it.
   * @param limit - Page size override, falling back to the state page size.
   * @returns A promise resolving once the devices have been written to state.
   */
  const fetchData = async ({
    updatedFilterObject = {},
    isFetchMoreByClickingNext = false,
    limit,
  }: any) => {
    filterObject.current.facilityIds =
      selectedFacility.value !== GETTEXT_ALL_FACILITIES ? [selectedFacility.value] : undefined;

    if (!isFetchMoreByClickingNext) {
      _.assign(filterObject.current, {
        deviceIdCursor: undefined,
      });
    }

    const queryFilter = { ...filterObject.current, ...updatedFilterObject };
    filterObject.current = { ...queryFilter };

    lastQueryFilter.current = _.cloneDeep(queryFilter);

    try {
      const devices = isAwaitingUploadSubtab
        ? await fetchAwaitingUploadDevices(
            formatFilterAwaitingUploadDevices(queryFilter),
            limit || state.sizePerPage,
          )
        : isLostOrInactiveTab || isNotInStudySubtab || isRmaSubtab
          ? await fetchRmaDevices(queryFilter, limit || state.sizePerPage)
          : isMaintenanceSubtab
            ? await fetchMaintenanceDevices(queryFilter, limit || state.sizePerPage)
            : await fetchClinicDevices(queryFilter, limit || state.sizePerPage);
      const newDevices: any = isAwaitingUploadSubtab
        ? awaitingUploadDevicesFormatter({ studies: devices } as any)
        : isLostOrInactiveTab || isNotInStudySubtab || isRmaSubtab
          ? rmaDevicesFormatter({
              devices,
              type,
            })
          : isMaintenanceSubtab
            ? maintenanceDevicesFormatter({ devices })
            : devicesFormatter({
                devices,
                type,
              });

      if (!_.isEqual(queryFilter, lastQueryFilter.current)) {
        setState({
          isLoadedData: true,
          isDisabledSearchInput: false,
        });
        return;
      }

      // *: Update search Redux
      updateSearchRedux();

      const setStateObject = {
        isEndOfData: newDevices.length < (limit || state.sizePerPage),
        isLoadedData: true,
        isDisabledSearchInput: false,
      };
      if (queryFilter.deviceIdCursor) {
        const diffDevices = _.differenceBy(newDevices, state.devices, 'deviceId');
        const appearedDevices = appearDataTable(state.devices, newDevices, 'deviceId');
        if (limit) {
          _.assign(setStateObject, {
            devices: appearedDevices,
            isEndOfData: newDevices.length < (limit || state.sizePerPage) || !diffDevices.length, // *: New devices is similar to current devices
          });
        } else {
          _.assign(setStateObject, {
            devices: appearedDevices,
            devicesPage:
              isFetchMoreByClickingNext && diffDevices.length > 0
                ? state.devicesPage + 1
                : state.devicesPage,
            isEndOfData: newDevices.length < (limit || state.sizePerPage) || !diffDevices.length,
          });
        }

        if (_.isEmpty(newDevices) && !_.isEmpty(state.devices)) {
          showMessageInfo({ content: t`No more devices` });
        }
      } else {
        let ticketCount = 0;
        // maintenance tab is not part of the device tab so it does not fetch ticket count
        if (!isMaintenanceSubtab) {
          ticketCount = (await fetchTicketCount(isAwaitingUploadSubtab, queryFilter)) ?? 0;
        }
        _.assign(setStateObject, {
          devices: newDevices,
          devicesPage: 1,
          deviceTicketCount: ticketCount || 0,
        });
      }

      setState(setStateObject);
    } catch (error: any) {
      showMessageError({ content: error.message });
      setState({
        devices: [],
        isLoadedData: true,
        isDisabledSearchInput: false,
      });
    }

    if (isFetchMoreByClickingNext) {
      needFetchMoreDevices.current = false;
    }
  };

  /**
   * Navigates from a device row to the studies list scoped to that device. Calls navigate on /studies carrying the deviceId in router state.
   *
   * @param deviceId - The device whose studies should be shown.
   * @returns Nothing.
   */
  const handleClickViewStudy = (deviceId: any) => {
    navigate('/studies', {
      state: {
        deviceId,
      },
    });
  };

  /**
   * Rebuilds the devices query filter from the current search bar selections. Clones the filter ref, routes the trimmed search text into deviceId or fwVersion according to the active search-by field while clearing the other, resets deviceIdCursor, maps the all-models choice to undefined, then stores the result back on the ref.
   *
   * @returns The rebuilt device filter object.
   */
  const handleCreateFilterObject = () => {
    const newFilterObject = _.cloneDeep(filterObject.current);
    if (searchByValueProp === 'deviceId') {
      newFilterObject.deviceId = searchValueProp?.trim() || undefined;
      newFilterObject.fwVersion = undefined;
    } else {
      newFilterObject.fwVersion = searchValueProp?.trim() || undefined;
      newFilterObject.deviceId = undefined;
    }

    _.assign(newFilterObject, {
      deviceIdCursor: undefined,
      model: modelProp === 'All models' ? undefined : modelProp,
    });

    filterObject.current = newFilterObject;

    return newFilterObject;
  };

  /**
   * Starts a Protect Plus RMA ticket for either a single device row or the current multi-selection. Given a device it navigates to /rma/new with that device and facility; otherwise it rejects selections exceeding the RMA limit config's maxDevicePerTicket with an error toast, then navigates with all checked devices and the first checked device's facility or the selected one.
   *
   * @param data - The device row to initiate for; omitted to use the checked devices.
   * @returns Nothing.
   */
  const handleClickInitiateProtectPlus = (data?: any) => {
    const { deviceId, model, facility } = data || {};
    if (deviceId) {
      navigate('/rma/new', {
        state: {
          devices: [{ deviceId, model }],
          facility,
        },
      });
    } else {
      if (state.checkedDevices?.length > (rmaLimitConfig.maxDevicePerTicket ?? Infinity)) {
        const maxDevicePerTicketText = rmaLimitConfig.maxDevicePerTicket || '';
        const deviceText = rmaLimitConfig.maxDevicePerTicket === 1 ? 'device' : 'devices';
        showMessageError({
          content: t`You can only select a maximum of ${maxDevicePerTicketText} ${deviceText}.`,
        });
        return;
      }
      const { label, value } = selectedFacility;
      navigate('/rma/new', {
        state: {
          devices: state.checkedDevices,
          facility: state.firstCheckedDeviceFacility || { id: value, name: label },
        },
      });
    }
  };

  /**
   * Clears the device multi-selection used to start an RMA ticket. Resets checkedDevices to an empty array and forgets the first checked device's facility.
   *
   * @returns Nothing.
   */
  const onClickRemoveAllSelectedDevices = () => {
    setState({
      checkedDevices: [],
      firstCheckedDeviceFacility: undefined,
    });
  };

  /**
   * Applies a search bar change to the devices table and refetches it. Blocks app scrolling, merges the incoming filter into the filter ref, marks the table as loading with the given input-disabled state, then refetches on the next tick, continuing pagination when a fetch-more was pending.
   *
   * @param updatedFilterObject - The filter changes reported by the search component.
   * @param isDisabledSearchInput - Whether the search input stays disabled while loading.
   * @param _unusedArg - Unused third argument from the search component.
   * @returns Nothing.
   */
  const handleChangeSearchComponent = (
    updatedFilterObject: any,
    isDisabledSearchInput: any,
    _unusedArg?: any,
  ) => {
    preventAppScroll(true);
    _.assign(filterObject.current, { ...updatedFilterObject });
    setState({
      isLoadedData: false,
      isDisabledSearchInput,
    });
    setTimeout(() => {
      fetchData({
        updatedFilterObject: filterObject.current,
        isFetchMoreByClickingNext: needFetchMoreDevices.current,
      });
    }, 0);
  };

  /**
   * Opens the study-info drawer for the study referenced by a device row. Sets isDrawerStudyInfo to true and stores the given id as fetchStudyId so the drawer loads that study.
   *
   * @param id - The study id the drawer should fetch.
   * @returns Nothing.
   */
  const drawerStudyInfo = (id: any) => {
    setState({
      isDrawerStudyInfo: true,
      fetchStudyId: id,
    });
  };

  /**
   * Closes the study-info drawer opened from a device row. Sets isDrawerStudyInfo to false.
   *
   * @returns Nothing.
   */
  const closeDrawer = () => {
    setState({ isDrawerStudyInfo: false });
  };

  /**
   * Assembles the devices table column set for the current device table type. Defines the shared device-id, model, firmware, status, lifetime, network, charging, facility, last-connect, last-study and last-used definitions, then per type returns a specific ordering with tuned widths, appends the facility column only when isShowFacilityName is set, and on the maintenance tab adds a right-fixed action column that initiates Protect Plus.
   *
   * @returns The ordered column definitions for the active table type.
   */
  const generateColumns = () => {
    /**
     * Builds the sortable, left-fixed Device ID column of the devices table. Derives its sort indicator from the active filter's sort order, delegates rendering to the device-id formatter with the isLoadedData flag, the renewal tag enabled and any caller extra data, and lets the option argument override width and fixed settings.
     *
     * @param option - Column overrides merged over the generated definition.
     * @param extraData - Additional values merged into the formatter's extra-data argument.
     * @returns The Device ID column definition.
     */
    const deviceId = (option = {}, extraData = {}) => ({
      dataIndex: 'deviceId',
      title: `${t`Device ID`}`,
      sorter: true,
      sortOrder: convertSortOrder('deviceId', filterObject.current),
      sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
      render: (...props: any[]) =>
        (deviceIdFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
          isShowRenewTag: true,
          ...extraData,
        }),
      width: '12.2%',
      minWidth: '168px',
      fixed: 'left',
      ...option,
    });
    const model: any = {
      dataIndex: isAwaitingUploadSubtab ? 'deviceType' : 'deviceModel',
      title: `${t`Model`}`,
      render: (...props: any[]) =>
        (modelFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
        }),
    };
    /**
     * Builds the firmware-version column of the devices table, reading its value from the row's lastSync.fwVersion. Delegates rendering to the firmware formatter with the hook's isLoadedData flag and spreads any caller overrides over the definition.
     *
     * @param option - Column overrides merged over the generated definition.
     * @returns The firmware-version column definition.
     */
    const fwVersion = (option = {}) => ({
      dataIndex: ['lastSync', 'fwVersion'],
      title: `${t`Firmware version`}`,
      render: (...props: any[]) =>
        (fwVersionFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
        }),
      ...option,
    });
    const status: any = {
      dataIndex: 'status',
      title: `${t`Status`}`,
      render: (...props: any[]) =>
        (statusFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
          eventsNoti: (props as any).eventsNoti,
        }),
    };
    const lifetime: any = {
      dataIndex: 'lifetime',
      title: `${t`Device lifetime`}`,
      width: '17%',
      render: (...props: any[]) =>
        (operationStatusFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
        }),
    };
    const networkMode: any = {
      dataIndex: ['lastSync', 'networkMode'],
      title: `${t`Network`}`,
      width: '10%',
      render: (...props: any[]) =>
        (networkModeFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
        }),
    };
    const isCharging: any = {
      dataIndex: ['lastSync', 'isCharging'],
      title: `${t`Charging state`}`,
      minWidth: '150px',
      render: (...props: any[]) =>
        (chargingStateFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
        }),
    };
    const facilityName: any = {
      dataIndex: ['facility', 'name'],
      title: `${t`Facility`}`,
      render: (...props: any[]) =>
        (facilityFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
        }),
    };
    /**
     * Builds the last-connect column of the devices table, bound to the row's lastConnect field. Delegates rendering to the last-connect formatter, passing the isLoadedData flag, the view-study navigation handler and any caller-supplied extra data.
     *
     * @param extraData - Additional values merged into the formatter's extra-data argument.
     * @returns The last-connect column definition.
     */
    const lastConnect = (extraData = {}) => ({
      dataIndex: 'lastConnect',
      title: `${t`Last connect`}`,
      render: (...props: any[]) =>
        (lastConnectFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
          handleClickViewStudy,
          ...extraData,
        }),
    });
    const lastStudyId: any = {
      dataIndex: 'lastStudyId',
      width: '20%',
      title: 'Last study',
      render: (...props: any[]) =>
        (deviceLastStudyIdFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
          drawerStudyInfo,
        }),
    };
    const lastUsed: any = {
      dataIndex: 'lastUsed',
      title: 'Last used',
      width: '20%',
      render: (...props: any[]) =>
        (deviceLastUsedFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
        }),
    };
    const currentStudy: any = {
      dataIndex: 'lastStudyId',
      title: 'Current study',
      render: (...props: any[]) =>
        (deviceLastStudyIdFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
          drawerStudyInfo,
        }),
    };
    const lastStudy: any = {
      dataIndex: 'lastStudyId',
      title: 'Last study',
      render: (...props: any[]) =>
        (deviceLastStudyIdFormatter as any)(...props, {
          isLoadedData: state.isLoadedData,
          drawerStudyInfo,
        }),
    };

    switch (type) {
      case DEVICE_TABLE_TYPE.IN_STUDY: {
        const columns: any[] = [
          deviceId(
            {},
            {
              eventsNoti,
              toggleDisplayDrawer,
            },
          ),
        ];
        model.width = '180px';
        status.width = '9.1%';
        networkMode.width = '9.1%';
        columns.push(model, currentStudy, fwVersion(), status, networkMode, isCharging);
        if (state.isShowFacilityName) {
          facilityName.width = '23.8%';
          columns.push(facilityName);
        }

        return columns;
      }
      case DEVICE_TABLE_TYPE.AWAITING_UPLOAD: {
        const columns: any[] = [
          deviceId(
            { width: undefined },
            {
              eventsNoti,
              toggleDisplayDrawer,
            },
          ),
          model,
          currentStudy,
          lastConnect(),
          fwVersion(),
        ];
        if (state.isShowFacilityName) {
          facilityName.width = '23.8%';
          columns.push(facilityName);
        }
        return columns;
      }
      case DEVICE_TABLE_TYPE.RMA: {
        const columns: any[] = [
          deviceId(
            { width: undefined },
            {
              eventsNoti,
              toggleDisplayDrawer,
            },
          ),
          model,
          lastStudy,
          lastConnect(),
          fwVersion(),
        ];
        if (state.isShowFacilityName) {
          facilityName.width = '23.8%';
          columns.push(facilityName);
        }
        return columns;
      }
      case DEVICE_TABLE_TYPE.LOST_INACTIVE: {
        const columns: any[] = [
          deviceId(
            {},
            {
              eventsNoti,
              toggleDisplayDrawer,
            },
          ),
          model,
          lastStudy,
          lastConnect(),
          fwVersion(),
        ];
        if (state.isShowFacilityName) {
          facilityName.width = '38%';
          columns.push(facilityName);
        }
        return columns;
      }
      case DEVICE_TABLE_TYPE.NOT_IN_STUDY: {
        const columns: any[] = [
          deviceId(
            { width: undefined },
            {
              eventsNoti,
              toggleDisplayDrawer,
            },
          ),
          model,
          lastStudy,
          lastConnect(),
          fwVersion(),
        ];
        if (state.isShowFacilityName) {
          facilityName.width = '23.8%';
          columns.push(facilityName);
        }
        return columns;
      }
      case DEVICE_TABLE_TYPE.UNUSED_ALL:
      case DEVICE_TABLE_TYPE.UNUSED_ONEMONTH:
      case DEVICE_TABLE_TYPE.UNUSED_TWOMONTH:
      case DEVICE_TABLE_TYPE.UNUSED_THREEMONTH:
      case DEVICE_TABLE_TYPE.UNUSED_SIXMONTH:
      case DEVICE_TABLE_TYPE.UNUSED_ONEYEAR: {
        const columns: any[] = [
          deviceId(
            { width: undefined },
            {
              eventsNoti,
              toggleDisplayDrawer,
            },
          ),
          model,
          lastStudyId,
          lastUsed,
          lastConnect(),
          fwVersion(),
        ];
        return columns;
      }
      case DEVICE_TABLE_TYPE.MAINTENANCE: {
        // model.width = '9.1%';
        status.width = '11.4%';
        networkMode.width = '9.1%';
        lifetime.width = undefined;
        model.minWidth = '180px';
        status.minWidth = '120px';
        networkMode.minWidth = '120px';
        lifetime.minWidth = '150px';
        const columns: any[] = [
          deviceId(
            {},
            {
              eventsNoti,
              toggleDisplayDrawer,
            },
          ),
          model,
          fwVersion({ minWidth: '150px' }),
          status,
          networkMode,
          isCharging,
          lifetime,
        ];
        if (state.isShowFacilityName) {
          facilityName.minWidth = 'calc(100% - 1134px)';
          columns.push(facilityName);
        }
        const action: any = {
          dataField: 'actionIcon',
          render: (...props: any[]) =>
            (actionIconFormatter as any)(...props, {
              isLoadedData: state.isLoadedData,
              handleClickInitiateProtectPlus,
            }),
          width: '48px',
          fixed: 'right',
          onHeaderCell: () => ({ 'aria-label': t`Actions` }),
        };
        columns.push(action);
        return columns;
      }
      default: {
        // *: Default is DEVICE_TABLE_TYPE ALl
        const columns: any[] = [
          deviceId(
            {},
            {
              eventsNoti,
              toggleDisplayDrawer,
            },
          ),
          model,
          fwVersion(),
        ];
        status.width = '9.1%';
        networkMode.width = '9.1%';
        columns.push(status, networkMode, isCharging, lastConnect());
        if (state.isShowFacilityName) {
          facilityName.width = '23.8%';
          columns.push(facilityName);
        }
        return columns;
      }
    }
  };

  /**
   * Handles sorting, page-size and page changes for the devices table. A changed sorter stores the asc or desc order, clears the cursor and refetches; a changed page size refetches from the first page with the new limit; paging past the loaded rows sets the deviceId cursor and fetches more; otherwise the page simply moves, toasting that there are no more devices at the end of data.
   *
   * @param pagination - The pagination state carrying the requested page and page size.
   * @param filters - The antd column filters, unused.
   * @param sorter - The antd sorter state used to detect an order change.
   * @returns Nothing.
   */
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const isChangedOrder = checkIsChangedSorter(sorter, filterObject.current);

    if (isChangedOrder) {
      const { order } = sorter;
      const sortOrder = !order || order === 'ascend' ? 'asc' : 'desc';
      filterObject.current.sortOrder = sortOrder;
      filterObject.current.deviceIdCursor = undefined;
      setState({
        isLoadedData: false,
        isDisabledSearchInput: true,
      });
      setTimeout(() => {
        fetchData({ updatedFilterObject: filterObject.current });
      }, 0);
      return;
    }

    const { current, pageSize } = pagination;
    const hasChangedPageSize = pageSize !== state.sizePerPage;
    if (hasChangedPageSize) {
      filterObject.current.deviceIdCursor = undefined;
      setState({
        sizePerPage: pageSize,
        devicesPage: 1,
        isDisabledSearchInput: true,
        isLoadedData: false,
      });
      setTimeout(() => {
        fetchData({
          updatedFilterObject: filterObject.current,
          limit: pageSize,
        });
      }, 0);
    } else if (current * pageSize > state.devices.length && !state.isEndOfData) {
      needFetchMoreDevices.current = true;
      filterObject.current.deviceIdCursor = state.devices?.[state.devices.length - 1]?.deviceId;
      setState({
        sizePerPage: pageSize,
        isDisabledSearchInput: true,
        isLoadedData: false,
      });
      const hasChangedSizePerPage = pageSize !== state.sizePerPage;
      setTimeout(() => {
        fetchData({
          updatedFilterObject: filterObject.current,
          isFetchMoreByClickingNext: true,
          limit: hasChangedSizePerPage ? current * pageSize - state.devices.length : null,
        });
      }, 0);
    } else {
      const ceilLength = Math.ceil(state.devices.length / pageSize) * pageSize;
      if (current * pageSize > ceilLength && state.isEndOfData) {
        showMessageInfo({ content: t`No more devices` });
      } else {
        setState({
          devicesPage: current,
          sizePerPage: pageSize,
        });
      }
    }
  };

  useEffect(() => {
    if (
      isActive &&
      ((!state.isLoadedData && !state.isEndOfData) || hasChangedSearchValue.current)
    ) {
      preventAppScroll(true);

      let filterObj: any = { deviceIdCursor: undefined };
      if (hasChangedSearchValue.current) {
        filterObj = handleCreateFilterObject();
        setState({ isLoadedData: false }); // *: Display skeleton
      }
      if (isUnused) {
        const lastUsedRange = filterUnusedValue(type);
        _.assign(filterObject.current, { lastUsedRange });
      }
      hasChangedSearchValue.current = false;
      setTimeout(() => {
        fetchData({ updatedFilterObject: filterObj });
      }, 0);
    }
  }, [isActive]);

  useEffect(() => {
    /**
     * Subscription to the emitter's last-sync channel that keeps row sync data live. Each message is forwarded to the last-sync updater, which patches the matching device in the loaded list.
     *
     * @returns The listener handle, used to remove the subscription on cleanup.
     */
    const lastSyncListener = emitter.addListener(EMITTER_CONSTANTS.LAST_SYNC, (msg: any) => {
      handleUpdateLastSync(msg);
    });
    /**
     * Subscription to the emitter's device-event channel that keeps row statuses live. Each message is forwarded to the status updater, which patches the matching device in the loaded list.
     *
     * @returns The listener handle, used to remove the subscription on cleanup.
     */
    const deviceStatusListener = emitter.addListener(EMITTER_CONSTANTS.DEVICE_EVENT, (msg: any) => {
      handleUpdateDeviceStatus(msg);
    });

    return () => {
      lastSyncListener.remove();
      deviceStatusListener.remove();
    };
  }, [state.devices]);

  useUpdateEffect(() => {
    if (
      (supportFacilities?.length ?? 0) > 0 ||
      (facilitySelection.userSupportFacilities()?.length ?? 0) > 0
    ) {
      if (isActive) {
        setState({
          isLoadedData: false,
          isDisabledSearchInput: true,
          isShowFacilityName: selectedFacility.value === GETTEXT_ALL_FACILITIES,
        });
        setTimeout(() => {
          fetchData({ updatedFilterObject: { deviceIdCursor: undefined } });
        }, 0);
      } else {
        setState({
          isLoadedData: false,
          isEndOfData: false,
          isShowFacilityName: selectedFacility.value === GETTEXT_ALL_FACILITIES,
        });
      }
    }
    setState({
      checkedDevices: [],
      firstCheckedDeviceFacility: undefined,
    });
  }, [selectedFacility.value]);
  // *: Use props.selectedFacility.value instead of props.selectedFacility because of Redux new object returned

  useUpdateEffect(() => {
    if (
      searchByValueProp !== searchByValue.current &&
      searchValueProp === searchValue.current &&
      !searchValueProp
    ) {
      // *: Change searchByValue and do not change searchValue
      searchByValue.current = searchByValueProp || 'deviceId';
      return;
    }

    searchValue.current = searchValueProp || '';
    searchByValue.current = searchByValueProp || 'deviceId';

    hasChangedSearchValue.current = true;

    if (isActive) {
      const filterObject = handleCreateFilterObject();
      handleChangeSearchComponent(filterObject, false, !searchValueProp);
      hasChangedSearchValue.current = false;
    }
  }, [searchByValueProp, searchValueProp, modelProp]);

  useUpdateEffect(() => {
    if (state.isLoadedData) {
      preventAppScroll(false);
    }
  }, [state.isLoadedData]);

  return {
    state,
    isMaintenanceSubtab,
    checkedDeviceIds,
    rowSelection,
    filterObject,
    closeDrawer,
    onClickRemoveAllSelectedDevices,
    handleClickInitiateProtectPlus,
    generateColumns,
    handleTableChange,
  };
};
